<i18n locale="en">
{
  "current-plan": "Current plan",
  "features-column": "Features",
  "included": "Included in {plan}",
  "not-included": "Not included in {plan}"
}
</i18n>
<template>
  <table
    class="plan-comparison-table w-full"
    data-testid="plan-comparison-table"
  >
    <caption v-if="caption" class="plan-comparison-table__caption text-left">
      <OwnType :text="caption" variant="title-small" color="primary" />
    </caption>
    <thead>
      <tr>
        <th class="text-left" scope="col">
          <OwnType
            :text="t('features-column')"
            variant="title-small"
            color="secondary"
          />
        </th>
        <th
          v-for="plan in plans"
          :key="plan"
          class="plan-comparison-table__plan-column"
          scope="col"
        >
          <span class="plan-comparison-table__plan-heading">
            <OwnType :text="plan" variant="title-small" color="secondary" />
            <span
              v-if="plan === currentPlan"
              class="plan-comparison-table__current-plan"
              data-testid="plan-comparison-current-plan"
            >
              {{ t('current-plan') }}
            </span>
          </span>
        </th>
      </tr>
    </thead>
    <tbody>
      <template v-for="section in sections" :key="section.title">
        <tr>
          <th
            class="plan-comparison-table__section text-left"
            :colspan="plans.length + 1"
            scope="colgroup"
          >
            <OwnType
              :text="section.title"
              variant="title-very-small"
              color="secondary"
            />
          </th>
        </tr>
        <tr
          v-for="row in section.rows"
          :key="row.name"
          :data-testid="`plan-comparison-row-${toTestId(row.name)}`"
        >
          <th class="text-left" scope="row">
            <OwnType :text="row.name" variant="paragraph-small" />
          </th>
          <td
            v-for="(plan, planIndex) in plans"
            :key="plan"
            class="plan-comparison-table__availability"
          >
            <span class="plan-comparison-table__mark">
              <Checkmark1
                v-if="row.availability[planIndex]"
                :size="16"
                :aria-label="t('included', { plan })"
                role="img"
              />
              <MinusLarge
                v-else
                class="plan-comparison-table__not-included"
                :size="16"
                :aria-label="t('not-included', { plan })"
                role="img"
              />
            </span>
          </td>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<script lang="ts" setup>
import { Checkmark1 } from '@owner/icons-vue/Checkmark1'
import { MinusLarge } from '@owner/icons-vue/MinusLarge'
import { useI18n } from 'vue-i18n'

import { OwnType } from '@/ui/OwnType'

import type { PlanComparisonSection } from '../types'
import { toTestId } from '../utils'

withDefaults(
  defineProps<{
    caption?: string
    /** Plan name that gets the "Current plan" pill in its column header. */
    currentPlan?: string
    plans: string[]
    sections: PlanComparisonSection[]
  }>(),
  {
    caption: undefined,
    currentPlan: undefined,
  }
)

const { t } = useI18n()
</script>

<style lang="scss" scoped>
// Dark card table from the tier-upgrades dark design (Figma node 428:30754):
// translucent #222 surface, hairline #484848 dividers, sticky #222 header.
// Fixed palette, not theme tokens — it always sits on the dark upsell surface.
.plan-comparison-table {
  max-width: 800px;
  border-collapse: separate;
  border-spacing: 0;
  border: 0.5px solid #484848;
  border-radius: 16px;
  background-color: rgb(34 34 34 / 50%);

  &__caption {
    padding-bottom: 16px;
  }

  th,
  td {
    padding: 8px 16px;
    border-bottom: 0.5px solid #484848;
  }

  // The sticky header escapes an `overflow: hidden` clip on the table, so the
  // rounded corners live on the corner cells instead.
  thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    padding: 12px 16px 8px;
    background-color: #222;

    &:first-child {
      border-top-left-radius: 16px;
    }

    &:last-child {
      border-top-right-radius: 16px;
    }
  }

  tbody tr:last-child {
    th,
    td {
      border-bottom: none;
    }

    th:first-child {
      border-bottom-left-radius: 16px;
    }

    td:last-child {
      border-bottom-right-radius: 16px;
    }
  }

  &__plan-heading {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  &__current-plan {
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 500;
    line-height: 14px;
    white-space: nowrap;
    color: #fff;
    background-color: rgb(255 255 255 / 16%);
  }

  &__section {
    background-color: rgb(34 34 34 / 50%);
  }

  &__plan-column,
  &__availability {
    width: 100px;
    text-align: center;
  }

  &__availability {
    color: var(--text-color-secondary);
  }

  // The check icon renders as a block-level svg, so text-align alone leaves it
  // left-aligned; flex-center both marks so checks and dashes line up.
  &__mark {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__not-included {
    color: var(--text-color-tertiary);
  }
}
</style>
